import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useRole } from "@/contexts/RoleContext";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/hooks/useTheme";
import { LanguageSwitcher } from "@/components/ui/LanguageSwitcher";
import logoLight from "@/assets/SRB.jpg";
import logoDark from "@/assets/SKD.jpg";

interface V2HeaderProps {
  /** État replié de la sidebar (desktop). */
  sidebarCollapsed?: boolean;
  /** Toggle de la sidebar (desktop). */
  onToggleSidebar?: () => void;
}

export function V2Header({ sidebarCollapsed, onToggleSidebar }: V2HeaderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { role } = useRole();
  const { profile } = useAuth();
  const { theme } = useTheme();

  // Création de bien CONTEXTUELLE (décision user 2026-07-12) : le bouton du
  // catalogue Location crée un bien location, celui du catalogue Vente un
  // bien vente — plus de modale de choix à la création (elle ne survit que
  // pour la duplication). Location : C-Level + admin (décision 2026-06-02).
  // Vente : équipe Sales (rôle `vente`) + C-Level + admin.
  const canCreateLocation = (role === "c_level" || role === "admin") && pathname === "/v2/biens";
  const canCreateVente =
    (role === "vente" || role === "c_level" || role === "admin") && pathname === "/v2/biens-vente";
  const canCreate = canCreateLocation || canCreateVente;
  const createTarget = canCreateVente ? "/v2/biens-vente/new" : "/v2/biens/new?transaction=location";
  const isPrestataire = role === "prestataire";

  return (
    <header className="sticky top-0 z-30 bg-surface border-b border-border h-14 px-4 md:px-6 flex items-center gap-3">
      {/* Toggle sidebar (desktop) — replie/déplie le menu de gauche pour gagner de la place */}
      {onToggleSidebar && (
        <button
          type="button"
          onClick={onToggleSidebar}
          title={sidebarCollapsed ? t("v2.header.showMenu") : t("v2.header.hideMenu")}
          aria-label={sidebarCollapsed ? t("v2.header.showMenu") : t("v2.header.hideMenu")}
          className="hidden md:inline-flex items-center justify-center w-8 h-8 rounded-md text-text-muted hover:bg-gray-100 hover:text-text-primary cursor-pointer flex-shrink-0"
        >
          {sidebarCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>
      )}
      {/* Logo mobile only — sidebar le porte sur desktop */}
      {isPrestataire && (
        <img
          src={theme === "dark" ? logoDark : logoLight}
          alt="Skyline"
          className="h-8 md:h-10 object-contain"
        />
      )}

      <div className="flex items-center gap-2 ml-auto">
        <LanguageSwitcher />
        {canCreate && (
          <button
            type="button"
            onClick={() => navigate(createTarget)}
            className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent text-white text-sm font-medium hover:opacity-90 cursor-pointer"
          >
            <Plus size={14} /> {t("biens.newBien")}
          </button>
        )}

        {profile && (
          <div className="hidden md:flex items-center gap-2 px-2 py-1 rounded-md bg-background">
            <div className="w-7 h-7 rounded-full bg-accent/15 text-accent flex items-center justify-center text-xs font-semibold">
              {(profile.first_name || "?")[0]}
              {(profile.last_name || "")[0]}
            </div>
            <div className="text-xs leading-tight">
              <div className="font-medium text-text-primary">
                {profile.first_name} {profile.last_name}
              </div>
              <div className="text-text-muted">{profile.email}</div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
